import { getFirestore, doc, getDoc, collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { getDb } from './firebase';

let db = null;

// Cache mekanizması
const cache = new Map();
const CACHE_DURATION = 15 * 60 * 1000; // 15 dakika
const ADMIN_CACHE_DURATION = 60 * 60 * 1000; // Admin için 60 dakika (30 dakikadan artırıldı)

// Önbellek durumunu izle
let cacheInitialized = false;
let isInitializing = false;

// Cache'i başlat
export const init = async () => {
  // Eğer zaten başlatılıyorsa veya başlatılmışsa, tekrar başlatma
  if (isInitializing || cacheInitialized) {
    return true;
  }
  
  isInitializing = true;
  
  try {
    // Admin sayfasında isek, önbelleği daha az agresif kullan
    const isAdminPage = window.location.pathname.startsWith('/admin');
    
    if (!isAdminPage) {
      try {
        db = await getDb();
        
        // Sayfa yüklendiğinde sık kullanılan koleksiyonları önceden yükle
        setTimeout(() => {
          preloadCommonCollections();
        }, 1000); // 1 saniye gecikme ile yükle
      } catch (error) {
        console.error('Cache DB initialization error:', error);
      }
    } else {
      // Admin sayfasında isek, db'yi geciktirerek başlat
      setTimeout(async () => {
        try {
          db = await getDb();
        } catch (error) {
          console.error('Admin cache DB initialization error:', error);
        }
      }, 2000); // 2 saniye gecikme ile başlat
    }
    
    cacheInitialized = true;
    return true;
  } catch (error) {
    console.error('Cache initialization error:', error);
    return false;
  } finally {
    isInitializing = false;
  }
};

// Sık kullanılan koleksiyonları önceden yükle
const preloadCommonCollections = async () => {
  try {
    // Sadece admin sayfasında isek admin verilerini önceden yükle
    if (window.location.pathname.startsWith('/admin')) {
      // Admin sayfasında isek, sadece gerekli verileri yükle
      const path = window.location.pathname;
      
      if (path.includes('/jobs')) {
        // Jobs sayfasında isek, jobs koleksiyonunu yükle
        getCollectionData('jobs', { limit: 20 });
      } else if (path.includes('/blogs')) {
        // Blogs sayfasında isek, blogs koleksiyonunu yükle
        getCollectionData('blogs', { limit: 10 });
      } else if (path.includes('/users')) {
        // Users sayfasında isek, users koleksiyonunu yükle
        getCollectionData('users', { limit: 10 });
      } else if (path.includes('/job-applications')) {
        // Job applications sayfasında isek, job_applications koleksiyonunu yükle
        getCollectionData('job_applications', { limit: 10 });
      } else if (path.includes('/talent-talks')) {
        // Talent talks sayfasında isek, talent_talks koleksiyonunu yükle
        getCollectionData('talent_talks', { limit: 10 });
      }
    } else {
      // Admin sayfasında değilsek, jobs koleksiyonunu yükle
      getCollectionData('jobs', { limit: 10 });
    }
  } catch (error) {
    console.error('Preloading error:', error);
  }
};

// Otomatik temizlemeyi başlat
export const startAutoCleanup = () => {
  // Daha az sıklıkta temizlik yap
  setInterval(() => {
    const now = Date.now();
    let count = 0;
    
    for (const [key, value] of cache.entries()) {
      const duration = key.startsWith('admin-') ? ADMIN_CACHE_DURATION : CACHE_DURATION;
      if (now - value.timestamp > duration) {
        cache.delete(key);
        count++;
      }
    }
    
    if (count > 0 && window.location.pathname.startsWith('/admin')) {
      console.log(`Cache cleanup: ${count} items removed`);
    }
  }, 10 * 60 * 1000); // Her 10 dakikada bir kontrol et (5 dakikadan artırıldı)
};

// Cache'den veri al
export const getCachedData = (key) => {
  return getFromCache(key);
};

// Cache'i temizle
export const clearCache = (prefix = '') => {
  if (!prefix) {
    cache.clear();
    return;
  }
  
  // Belirli bir önek ile başlayan tüm önbellek girdilerini temizle
  for (const key of cache.keys()) {
    if (key.startsWith(prefix)) {
      cache.delete(key);
    }
  }
};

// Veriyi cache'den al
const getFromCache = (key) => {
  const cached = cache.get(key);
  if (!cached) return null;
  
  const now = Date.now();
  const duration = key.startsWith('admin-') ? ADMIN_CACHE_DURATION : CACHE_DURATION;
  if (now - cached.timestamp > duration) {
    cache.delete(key);
    return null;
  }
  
  return cached.data;
};

// Veriyi cache'e kaydet
const setToCache = (key, data) => {
  cache.set(key, {
    data,
    timestamp: Date.now()
  });
};

// Koleksiyon verilerini getir - optimize edildi
export const getCollectionData = async (collectionName, options = {}) => {
  try {
    const isAdminRequest = window.location.pathname.startsWith('/admin');
    const cachePrefix = isAdminRequest ? 'admin-' : '';
    const cacheKey = `${cachePrefix}${collectionName}-${JSON.stringify(options)}`;
    
    // Önbellekten veri al
    const cachedData = getFromCache(cacheKey);
    if (cachedData) {
      // Önbellekte veri varsa, asenkron olarak güncel veriyi kontrol et
      // ama önce önbellekteki veriyi döndür
      if (isAdminRequest) {
        // Admin sayfasında isek, daha az sıklıkta güncelle
        setTimeout(() => refreshCacheIfNeeded(cacheKey, collectionName, options), 5000);
      } else {
        setTimeout(() => refreshCacheIfNeeded(cacheKey, collectionName, options), 1000);
      }
      return cachedData;
    }

    // Önbellekte veri yoksa, veritabanından al
    if (!db) {
      // Admin sayfasında isek ve db yoksa, boş dizi döndür
      // UI'ın hızlı yüklenmesi için
      if (isAdminRequest) {
        const emptyData = [];
        setToCache(cacheKey, emptyData);
        
        // Arka planda db'yi başlat ve veriyi al
        setTimeout(async () => {
          try {
            const dbInstance = await getDb();
            if (dbInstance) {
              db = dbInstance;
              refreshCacheIfNeeded(cacheKey, collectionName, options);
            }
          } catch (error) {
            console.error('Lazy DB initialization error:', error);
          }
        }, 100);
        
        return emptyData;
      }
      
      try {
        const dbInstance = await getDb();
        if (!dbInstance) {
          console.error('Failed to initialize Firestore in getCollectionData');
          return [];
        }
        db = dbInstance;
      } catch (error) {
        console.error('Error initializing Firestore:', error);
        return [];
      }
    }
    
    if (!db) {
      console.error('Firestore instance still not available');
      return [];
    }
    
    try {
      const collectionRef = collection(db, collectionName);
      
      let q = collectionRef;
      
      // Filtreleri uygula
      if (options.filters) {
        const filters = options.filters.map(f => where(f.field, f.operator, f.value));
        q = query(q, ...filters);
      }
      
      // Sıralama uygula
      if (options.orderBy) {
        q = query(q, orderBy(options.orderBy.field, options.orderBy.direction));
      }
      
      // Limit uygula
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setToCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching collection data:', error);
      return [];
    }
  } catch (error) {
    console.error('Error in getCollectionData:', error);
    return []; // Hata durumunda boş dizi döndür
  }
};

// Önbelleği gerekirse güncelle
const refreshCacheIfNeeded = async (cacheKey, collectionName, options) => {
  try {
    const cached = cache.get(cacheKey);
    if (!cached) return;
    
    const now = Date.now();
    const isAdminCache = cacheKey.startsWith('admin-');
    const duration = isAdminCache ? ADMIN_CACHE_DURATION / 2 : CACHE_DURATION / 2;
    
    // Önbellek süresinin yarısı geçmişse, arka planda güncelle
    if (now - cached.timestamp > duration) {
      if (!db) {
        db = await getDb();
      }
      
      const collectionRef = collection(db, collectionName);
      
      let q = collectionRef;
      
      // Filtreleri uygula
      if (options.filters) {
        const filters = options.filters.map(f => where(f.field, f.operator, f.value));
        q = query(q, ...filters);
      }
      
      // Sıralama uygula
      if (options.orderBy) {
        q = query(q, orderBy(options.orderBy.field, options.orderBy.direction));
      }
      
      // Limit uygula
      if (options.limit) {
        q = query(q, limit(options.limit));
      }
      
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setToCache(cacheKey, data);
    }
  } catch (error) {
    console.error('Error refreshing cache:', error);
  }
};

// Tek döküman verisi getir
export const getDocumentData = async (collectionName, documentId) => {
  try {
    const isAdminRequest = window.location.pathname.startsWith('/admin');
    const cachePrefix = isAdminRequest ? 'admin-' : '';
    const cacheKey = `${cachePrefix}${collectionName}-${documentId}`;
    
    const cachedData = getFromCache(cacheKey);
    if (cachedData) return cachedData;

    // Admin sayfasında isek ve db yoksa, null döndür
    // UI'ın hızlı yüklenmesi için
    if (isAdminRequest && !db) {
      // Arka planda db'yi başlat ve veriyi al
      setTimeout(async () => {
        try {
          const dbInstance = await getDb();
          if (dbInstance) {
            db = dbInstance;
            refreshDocumentCache(cacheKey, collectionName, documentId);
          }
        } catch (error) {
          console.error('Lazy DB initialization error:', error);
        }
      }, 100);
      
      return null;
    }

    if (!db) {
      try {
        const dbInstance = await getDb();
        if (!dbInstance) {
          console.error('Failed to initialize Firestore in getDocumentData');
          return null;
        }
        db = dbInstance;
      } catch (error) {
        console.error('Error initializing Firestore:', error);
        return null;
      }
    }
    
    if (!db) {
      console.error('Firestore instance still not available in getDocumentData');
      return null;
    }

    try {
      const docRef = doc(db, collectionName, documentId);
      const snapshot = await getDoc(docRef);
      
      if (!snapshot.exists()) {
        return null;
      }
      
      const data = {
        id: snapshot.id,
        ...snapshot.data()
      };
      
      setToCache(cacheKey, data);
      return data;
    } catch (error) {
      console.error('Error fetching document data:', error);
      return null;
    }
  } catch (error) {
    console.error('Error in getDocumentData:', error);
    return null; // Hata durumunda null döndür
  }
};

// Döküman önbelleğini güncelle
const refreshDocumentCache = async (cacheKey, collectionName, documentId) => {
  try {
    if (!db) {
      db = await getDb();
    }
    
    const docRef = doc(db, collectionName, documentId);
    const snapshot = await getDoc(docRef);
    
    if (!snapshot.exists()) {
      return;
    }
    
    const data = {
      id: snapshot.id,
      ...snapshot.data()
    };
    
    setToCache(cacheKey, data);
  } catch (error) {
    console.error('Error refreshing document cache:', error);
  }
}; 
